import api from './api';

export interface CostItemLineItem {
  recno?: number | null;
  qty: number;
  rate: number;
  paystatus: string;
  payreason: string;
  glcode: string;
  personresponsible: string;
  groupresponsible: string;
  total: number;
  deleted: 'Y' | 'N';
}

export interface CostItem {
  recno: number | null;
  pronumber: number;
  vendorcode: number;
  vendorName: string;
  invoicedate: string;
  invoicenumber: string;
  receiveddate: string;
  approvedby: string;
  approveddatetime: string;
  invoicetotal: number;
  tmwrate: number;
  costtype: string;
  numdays: number;
  reason: string;
  ponumber: string;
  dispute: 'Y' | 'N';
  disputereason: string;
  comments: string;
  masexportdate: string;
  status: string;
  holdtyperecno: number;
  holdtypeName: string;
  adjustamount: number;
  checknumber: string;
  creditrequesteddatetime: string;
  creditamountrequested: number;
  deleted: 'Y' | 'N';
  createdby: string;
  createdatetime: string;
  lineItems: CostItemLineItem[];
}

export interface CostItemPatch {
  vendorcode: number;
  invoicedate: string;
  invoicenumber: string;
  receiveddate: string;
  approvedby?: string;
  approveddatetime?: string;
  invoicetotal: number;
  tmwrate: number;
  costtype: string;
  numdays: number;
  reason: string;
  ponumber: string;
  dispute: 'Y' | 'N';
  disputereason: string;
  comments: string;
  status: string;
  holdtyperecno: number;
  adjustamount: number;
  checknumber: string;
  creditrequesteddatetime?: string;
  creditamountrequested: number;
  deleted: 'Y' | 'N';
  lineItems: CostItemLineItem[];
}

export interface CostItemReferenceData {
  costTypes: string[];
  statuses: string[];
  payStatuses: string[];
  departments: string[];
  reasons: string[];
  payReasons: string[];
  disputeReasons: string[];
  holdTypes: Array<{ recno: number; type: string }>;
  glCodes: Array<{ glcode: string; description: string }>;
}

export interface VendorOption {
  recno: number;
  name: string;
  mas90code: string;
  city: string;
  state: string;
}

/** The 8 bulk-editable Addl Cost columns (V1 updateall_add_*). */
export type AddlCostBulkColumn =
  | 'vendorcode' | 'invoicenumber' | 'invoicedate' | 'receiveddate'
  | 'ponumber' | 'status' | 'holdtyperecno' | 'checknumber';

export interface AddlCostChangeAllPreviewRow {
  recno: number;
  pronumber: number | string;
  eligible: boolean;
  errors: string[];
}

export interface AddlCostChangeAllResult {
  recno: number;
  pronumber: number | string;
  applied: boolean;
  errors: string[];
}

export const additionalCostItemsService = {
  /** Per-recno eligibility preview for a bulk Addl Cost edit across target PROs. */
  async changeAllPreview(
    pros: (number | string)[],
    fields: Partial<Record<AddlCostBulkColumn, unknown>>,
    signal?: AbortSignal,
  ): Promise<{ rows: AddlCostChangeAllPreviewRow[] }> {
    const res = await api.post(
      `/orders/additional-cost-items/change-all/preview`, { pros, fields }, { signal });
    return res.data;
  },

  /** Apply the flagged Addl Cost fields to every non-deleted cost record on the target
   *  PROs (per-recno validate + skip; (nonce, recno)-idempotent). */
  async changeAllApply(
    pros: (number | string)[],
    fields: Partial<Record<AddlCostBulkColumn, unknown>>,
    submitNonce: string,
  ): Promise<{ results: AddlCostChangeAllResult[] }> {
    const res = await api.post(
      `/orders/additional-cost-items/change-all/apply`, { pros, fields, submitNonce });
    return res.data;
  },

  async fetchReferenceData(pronumber: number): Promise<CostItemReferenceData> {
    const res = await api.get(`/orders/${pronumber}/additional-cost-items/reference-data`);
    return res.data;
  },

  async searchVendors(pronumber: number, q: string, limit = 20): Promise<VendorOption[]> {
    const res = await api.get(`/orders/${pronumber}/additional-cost-items/vendor-search`, {
      params: { q, limit },
    });
    return res.data;
  },

  async fetch(pronumber: number, recno: number): Promise<CostItem> {
    const res = await api.get(`/orders/${pronumber}/additional-cost-items/${recno}`);
    return res.data;
  },

  async create(pronumber: number, patch: CostItemPatch): Promise<CostItem> {
    const res = await api.post(`/orders/${pronumber}/additional-cost-items`, patch);
    return res.data;
  },

  async update(pronumber: number, recno: number, patch: CostItemPatch): Promise<CostItem> {
    const res = await api.put(`/orders/${pronumber}/additional-cost-items/${recno}`, patch);
    return res.data;
  },

  async delete(pronumber: number, recno: number): Promise<void> {
    await api.delete(`/orders/${pronumber}/additional-cost-items/${recno}`);
  },
};
